import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LOCAL_STORAGE_WORKSPACE_ID, TIME_FORMAT_WITH_HOUR } from 'config/constants';
import {
  AGGREGATED_ALARMS,
  EVENT_BY_ID,
  EVENT_COMMENT,
  EVENT_COUNT,
  EVENT_COUNT_ROOT,
  EVENT_FILTER_OPTIONS,
  EVENT_LIST,
  EVENT_PI_URL,
  EVENT_SIMILAR,
  EVENTS,
  EVENTS_AUTOMATIC_ACK_INFO,
  EVENTS_AUTOMATIC_ACK_MODELS,
  REASONS_BY_EVENT_ID,
  UPDATE_EVENT,
  UPDATE_EVENTS,
} from 'config/endpoints';
import dayjs from 'dayjs';
import { get, patch, post } from 'utils/axios';
import { EventComment, EventCount, EventList, EventReasonsOptions } from './contract';
import {
  AckModel,
  AggregatedAlarms,
  AggregatedAlarmsPayload,
  AutomaticAckInfo,
  EventBatchUpdateMutationProps,
  EventByIdProps,
  EventCommentMutationPayload,
  EventCommentMutationProps,
  EventCountProps,
  EventCountRootParams,
  EventCountRootProps,
  EventFilters,
  EventItem,
  EventItemPayload,
  EventListParams,
  EventListProps,
  EventSimilarPayload,
  EventUpdateMutationPayload,
  EventUpdateMutationProps,
  FilterOptionsResponse,
  GenericQueryOptions,
  PIAttributes,
  PIparams,
} from './types';

export const useInvalidateEventsQueries = (id?: string) => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries(['EVENTS']);
    queryClient.invalidateQueries(['EVENT_BY_ID', id]);
  };
};

export const useOptionsByEventQuery = (
  { eventId }: EventByIdProps,
  queryOptions?: GenericQueryOptions<EventReasonsOptions>,
) => {
  const formattedEventID = eventId?.replace('-CLONE', '');
  return useQuery(
    ['REASONS_BY_EVENT_ID', formattedEventID],
    get<EventReasonsOptions>(REASONS_BY_EVENT_ID(formattedEventID)),
    queryOptions,
  );
};

export const useEventQuery = (
  { eventId }: EventByIdProps,
  queryOptions?: GenericQueryOptions<EventItem & { data: Partial<EventItem> }>,
) => {
  const formattedEventID = eventId?.replace('-CLONE', '');
  return useQuery(
    ['EVENT_BY_ID', formattedEventID],
    get<EventItem & { data: Partial<EventItem> }>(EVENT_BY_ID(formattedEventID)),
    queryOptions,
  );
};
export const useAutomaticAckInfoQuery = (
  filters: EventFilters,
  queryOptions?: GenericQueryOptions<AutomaticAckInfo>,
) => {
  const { end_date, start_date } = filters;
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    ['AUTOMATIC_ACK_INFO', start_date, end_date],
    get(EVENTS_AUTOMATIC_ACK_INFO(workspaceId), { start_date, end_date }),
    queryOptions,
  );
};

export const useAutomaticAckModelsQuery = (
  filters: EventFilters,
  queryOptions?: GenericQueryOptions<{ models: AckModel[] }>,
) => {
  const { end_date, start_date } = filters;
  return useQuery(
    ['AUTOMATIC_ACK_MODELS', start_date, end_date],
    get(EVENTS_AUTOMATIC_ACK_MODELS, { alarm_start_date: start_date, alarm_end_date: end_date }),
    queryOptions,
  );
};

export const useEventListQuery = (
  props: EventListProps,
  queryOptions?: GenericQueryOptions<EventList>,
) =>
  useQuery(
    ['EVENT_LIST', props],
    get<EventList, EventListParams>(EVENT_LIST, {
      asset_id: props.assetId,
      status_id: props.statusId,
      model_name: props.modelName,
      date_from: props.startDate,
      date_to: props.endDate,
      show_analysis: props.showAnalysis,
      show_asset: props.showAsset,
      show_status: props.showStatus,
      show_tags: props.showTags,
    }),
    queryOptions,
  );

export const useEventCountRootQuery = (
  { onlyOpen }: EventCountRootProps,
  queryOptions?: GenericQueryOptions<EventCount>,
) =>
  useQuery(
    ['EVENT_COUNT_ROOT'],
    get<EventCount, EventCountRootParams>(EVENT_COUNT_ROOT, { only_open: onlyOpen }),
    queryOptions,
  );

export const useEventCountQuery = (
  { assetId, onlyOpen }: EventCountProps,
  queryOptions?: GenericQueryOptions<EventCount>,
) =>
  useQuery(
    ['EVENT_COUNT', assetId],
    get<EventCount, EventCountRootParams>(EVENT_COUNT(assetId), { only_open: onlyOpen }),
    queryOptions,
  );

export const useEventCommentQuery = (
  { eventId }: EventByIdProps,
  queryOptions?: GenericQueryOptions<EventComment>,
) => useQuery(['EVENT_COMMENT'], get<EventComment>(EVENT_COMMENT(eventId)), queryOptions);

export const useEventCommentMutation = () =>
  useMutation(({ eventId, comment }: EventCommentMutationProps) =>
    post<unknown, EventCommentMutationPayload>(EVENT_COMMENT(eventId), { payload: { comment } }),
  );
export const useSimilarEventsQuery = (
  {
    assetId,
    eventId,
    start_date,
    end_date,
  }: { assetId: string; eventId: string; start_date?: string; end_date?: string },
  queryOptions?: GenericQueryOptions<EventSimilarPayload>,
) => {
  const formattedEventID = eventId.replace('-CLONE', '');
  return useQuery(
    ['SIMILAR_EVENTS', assetId, formattedEventID, start_date, end_date],
    get(EVENT_SIMILAR(assetId, formattedEventID), {
      start_date: start_date ? dayjs(start_date).utc().format(TIME_FORMAT_WITH_HOUR) : undefined,
      end_date: end_date ? dayjs(end_date).utc().format(TIME_FORMAT_WITH_HOUR) : undefined,
    }),
    queryOptions,
  );
};
export const useEventsQuery = (
  { filters, include_descendants }: { filters: EventFilters; include_descendants?: boolean },
  queryOptions?: GenericQueryOptions<EventItemPayload>,
) => {
  const { assets, category, end_date, start_date, status, alarms_without_cds, closed_by } = filters;
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    [
      'EVENTS',
      {
        category,
        assets,
        status,
        start_date,
        end_date,
        alarms_without_cds,
        include_descendants,
        closed_by,
      },
    ],
    get(EVENTS(workspaceId), {
      start_date,
      end_date,
      status,
      event_categories: filters?.category?.join(','),
      ancestor_asset_ids: filters?.assets?.map((asset) => asset.id)?.join(','),
      alarms_without_cds,
      include_descendant_events: include_descendants,
      closed_by,
    }),
    { retry: false, enabled: workspaceId !== null, ...queryOptions },
  );
};

export const useAggregatedAlarmsQuery = (
  { alarmId }: AggregatedAlarms,
  queryOptions?: GenericQueryOptions<AggregatedAlarmsPayload>,
) => useQuery(['AGGREGATED_ALARMS', alarmId], get(AGGREGATED_ALARMS(alarmId)), queryOptions);

export const useUpdateEventMutation = (
  onSuccess?: (data: EventUpdateMutationPayload, variables: EventUpdateMutationProps) => void,
  onError?: () => void,
) =>
  useMutation(
    ({
      eventId,
      classification,
      next_step,
      not_useful_reason,
      comments,
      status,
      is_cd_treatment_required,
    }: EventUpdateMutationProps) =>
      patch<unknown, EventUpdateMutationPayload>(UPDATE_EVENT(eventId), {
        payload: {
          classification,
          next_step,
          not_useful_reason,
          comments,
          status,
          is_cd_treatment_required,
        },
      }),
    { onSuccess, onError },
  );

export const useUpdateBatchEventMutation = (
  onSuccess?: (data: EventUpdateMutationPayload, variables: EventBatchUpdateMutationProps) => void,
  onError?: () => void,
) =>
  useMutation(
    ({
      event_ids,
      classification,
      not_useful_reason,
      next_step,
      comments,
      status,
      is_cd_treatment_required,
    }: EventBatchUpdateMutationProps) =>
      patch<unknown, EventBatchUpdateMutationProps>(UPDATE_EVENTS, {
        payload: {
          event_ids,
          classification,
          next_step,
          not_useful_reason,
          comments,
          status,
          is_cd_treatment_required,
        },
      }),
    { onSuccess, onError },
  );

export const useFilterOptionsQuery = (
  queryOptions?: GenericQueryOptions<FilterOptionsResponse>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(['ALARM_LOADING', 'FILTER_OPTIONS'], get(EVENT_FILTER_OPTIONS(workspaceId)), {
    enabled: workspaceId !== null,
    ...queryOptions,
  });
};

export const useExternalReferenceUrl = (
  id: string,
  params: PIparams,
  queryOptions?: GenericQueryOptions<PIAttributes>,
) => {
  const queryData = useQuery([id], get(EVENT_PI_URL(id), params), queryOptions);
  if (queryData.isError) return { data: null };
  return queryData;
};
